import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Image as ImageIcon, Phone, Ticket, XCircle } from "lucide-react";
import { api, errMsg, fileUrl, fmtDate, rupiah } from "@/lib/api";
import { useApi, Skeleton, inputCls } from "@/components/portal/ui";
import { AdminTitle } from "@/pages/admin/AdminLayout";

export default function EventPayments() {
  const { data, reload } = useApi("/admin/tickets/pending");
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState("");

  const verify = async (reg, status, noteVal) => {
    setBusyId(reg.id);
    try {
      await api.post(`/admin/tickets/${reg.id}/verify`, { status, note: noteVal || undefined });
      toast.success(status === "active" ? "Tiket diverifikasi & diaktifkan" : "Pembayaran ditolak");
      setRejecting(null);
      setNote("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-event-payments">
      <AdminTitle eyebrow="Tiketing Event" title="Verifikasi Pembayaran Tiket" />
      {!data ? <Skeleton className="h-80" /> : data.length === 0 ? (
        <p className="text-slate-500" data-testid="event-payments-empty">Tidak ada pembayaran tiket yang menunggu verifikasi.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {data.map((r) => (
            <div key={r.id} className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-testid={`event-payment-${r.id}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] font-bold bg-indigo-50 text-indigo-700 rounded-full px-3 py-1 inline-flex items-center gap-1.5"><Ticket className="w-3 h-3" />{r.event_title}</span>
                <span className="text-xs text-slate-400">{fmtDate(r.created_at, { day: "numeric", month: "short", year: "numeric" })}</span>
              </div>
              <p className="mt-4 font-extrabold text-slate-900">{r.name}</p>
              <p className="text-sm text-slate-500 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{r.phone} · {r.email}</p>
              <p className="mt-2 text-lg font-black text-slate-900 tabular-nums">{rupiah(r.amount)}{r.ticket_type ? <span className="ml-2 text-xs font-semibold text-slate-400">{r.ticket_type}</span> : null}</p>
              {r.proof_url ? (
                <a href={fileUrl(r.proof_url)} target="_blank" rel="noreferrer" data-testid={`event-payment-proof-${r.id}`} className="mt-3 flex items-center gap-3 rounded-2xl border border-slate-100 p-3 hover:bg-slate-50 transition">
                  <img src={fileUrl(r.proof_url)} alt="Bukti pembayaran" className="w-14 h-14 rounded-xl object-cover" />
                  <span className="text-sm font-bold text-indigo-600 flex items-center gap-1.5"><ImageIcon className="w-4 h-4" />Lihat bukti pembayaran</span>
                </a>
              ) : (
                <p className="mt-3 text-xs text-slate-400">Tidak ada bukti pembayaran terlampir.</p>
              )}
              {rejecting === r.id ? (
                <div className="mt-4 space-y-2" data-testid={`event-payment-reject-form-${r.id}`}>
                  <textarea className={`${inputCls} !py-2.5 text-sm`} rows={2} placeholder="Alasan penolakan (opsional, dikirim ke jemaat via WA)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`event-payment-reject-note-${r.id}`} />
                  <div className="flex gap-2">
                    <button disabled={busyId === r.id} onClick={() => verify(r, "rejected", note)} data-testid={`event-payment-reject-confirm-${r.id}`} className="flex-1 bg-red-600 text-white rounded-xl py-2.5 text-sm font-bold hover:bg-red-700 disabled:opacity-40">Konfirmasi Tolak</button>
                    <button onClick={() => { setRejecting(null); setNote(""); }} data-testid={`event-payment-reject-cancel-${r.id}`} className="flex-1 bg-slate-100 text-slate-700 rounded-xl py-2.5 text-sm font-bold hover:bg-slate-200">Batal</button>
                  </div>
                </div>
              ) : (
                <div className="mt-5 flex gap-2">
                  <button disabled={busyId === r.id} onClick={() => verify(r, "active")} data-testid={`event-payment-approve-${r.id}`} className="flex-1 bg-emerald-50 text-emerald-700 rounded-xl py-2.5 text-sm font-bold hover:bg-emerald-100 disabled:opacity-40 inline-flex items-center justify-center gap-1.5"><CheckCircle2 className="w-4 h-4" />Verifikasi</button>
                  <button disabled={busyId === r.id} onClick={() => setRejecting(r.id)} data-testid={`event-payment-reject-${r.id}`} className="flex-1 bg-red-50 text-red-600 rounded-xl py-2.5 text-sm font-bold hover:bg-red-100 disabled:opacity-40 inline-flex items-center justify-center gap-1.5"><XCircle className="w-4 h-4" />Tolak</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

